import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Bell,
  Calendar,
  Clock,
  Send,
  AlertTriangle,
  CheckCircle,
  Filter,
  Search,
  Plus,
  Mail,
  MessageSquare,
  Eye,
  Trash2,
  X,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  FileText,
  DollarSign,
  Building2,
  Edit,
  Download
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Reminders = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState({ today: [], upcoming: [], overdue: [] });
  const [reminders, setReminders] = useState([]);
  const [clients, setClients] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [statutoryDates, setStatutoryDates] = useState([]);
  const [templates, setTemplates] = useState([]);
  
  const [activeTab, setActiveTab] = useState('dashboard');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [selectedReminder, setSelectedReminder] = useState(null);
  const [showStatutoryModal, setShowStatutoryModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showStatutoryEditModal, setShowStatutoryEditModal] = useState(false);
  const [editingStatutory, setEditingStatutory] = useState(null);
  const [statutoryForm, setStatutoryForm] = useState({
    name: '', description: '', due_day: 1, due_month: '', category: 'gst', applicable_to: []
  });
  
  // Form state
  const [reminderForm, setReminderForm] = useState({
    reminder_type: 'task_due',
    title: '',
    message: '',
    client_id: '',
    task_id: '',
    due_date: '',
    remind_days_before: [7, 3, 1, 0],
    recipients: [],
    send_to_client: true,
    priority: 'medium'
  });
  
  const [sendForm, setSendForm] = useState({
    message: '',
    send_email: true,
    send_whatsapp: true
  });

  const [reminderImageUrl, setReminderImageUrl] = useState(null);
  const [reminderImageBlob, setReminderImageBlob] = useState(null);
  const [loadingImage, setLoadingImage] = useState(false);

  const [templateForm, setTemplateForm] = useState({
    name: '',
    template_type: 'custom',
    subject: '',
    body: '',
    is_active: true
  });

  const isPartnerOrManager = user?.role === 'partner' || user?.role === 'manager';

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [dashboardRes, remindersRes, clientsRes, tasksRes, usersRes, statutoryRes, templatesRes] = await Promise.all([
        fetch(`${API_URL}/api/reminders/dashboard`, { headers }),
        fetch(`${API_URL}/api/reminders`, { headers }),
        fetch(`${API_URL}/api/clients`, { headers }),
        fetch(`${API_URL}/api/tasks`, { headers }),
        fetch(`${API_URL}/api/users`, { headers }).catch(() => ({ ok: false })),
        fetch(`${API_URL}/api/statutory-calendar`, { headers }),
        fetch(`${API_URL}/api/templates`, { headers }).catch(() => ({ ok: false }))
      ]);
      
      if (dashboardRes.ok) setDashboard(await dashboardRes.json());
      if (remindersRes.ok) setReminders(await remindersRes.json());
      if (clientsRes.ok) setClients(await clientsRes.json());
      if (tasksRes.ok) setTasks(await tasksRes.json());
      if (usersRes.ok) setUsers(await usersRes.json());
      if (statutoryRes.ok) setStatutoryDates(await statutoryRes.json());
      if (templatesRes.ok) setTemplates(await templatesRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateReminder = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/reminders`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(reminderForm)
      });
      
      if (response.ok) {
        setShowCreateModal(false);
        setReminderForm({
          reminder_type: 'task_due',
          title: '',
          message: '',
          client_id: '',
          task_id: '',
          due_date: '',
          remind_days_before: [7, 3, 1, 0],
          recipients: [],
          send_to_client: true,
          priority: 'medium'
        });
        fetchData();
      } else {
        const error = await response.json();
        alert('Failed to create reminder: ' + (error.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error creating reminder:', error);
      alert('Failed to create reminder');
    }
  };

  const handleSendReminder = async () => {
    if (!selectedReminder) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/reminders/${selectedReminder.id}/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(sendForm)
      });
      
      if (response.ok) {
        const result = await response.json();
        
        // Open WhatsApp if URL provided
        if (result.whatsapp_url && sendForm.send_whatsapp) {
          window.open(result.whatsapp_url, '_blank');
        }
        
        setShowSendModal(false);
        setSelectedReminder(null);
        fetchData();
        alert('Reminder sent successfully!');
      } else {
        const error = await response.json();
        alert('Failed to send reminder: ' + (error.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error sending reminder:', error);
      alert('Failed to send reminder');
    }
  };

  const updateReminderStatus = async (reminderId, status) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/reminders/${reminderId}?status=${status}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Error updating reminder:', error);
    }
  };

  const snoozeReminder = async (reminderId, days) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/reminders/${reminderId}?snooze_days=${days}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Error snoozing reminder:', error);
    }
  };

  const deleteReminder = async (reminderId) => {
    if (!window.confirm('Delete this reminder?')) return;
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/reminders/${reminderId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Error deleting reminder:', error);
    }
  };

  const seedStatutoryDates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/statutory-calendar/seed`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const result = await response.json();
        alert(result.message);
        fetchData();
      }
    } catch (error) {
      console.error('Error seeding statutory dates:', error);
    }
  };

  const openEditStatutory = (date) => {
    setEditingStatutory(date);
    setStatutoryForm({
      name: date.name,
      description: date.description || '',
      due_day: date.due_day,
      due_month: date.due_month || '',
      category: date.category,
      applicable_to: date.applicable_to || []
    });
    setShowStatutoryEditModal(true);
  };

  const handleSaveStatutory = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...statutoryForm,
        due_month: statutoryForm.due_month === '' ? null : parseInt(statutoryForm.due_month),
        due_day: parseInt(statutoryForm.due_day)
      };
      const url = editingStatutory
        ? `${API_URL}/api/statutory-calendar/${editingStatutory.id}`
        : `${API_URL}/api/statutory-calendar`;
      const method = editingStatutory ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (response.ok) {
        setShowStatutoryEditModal(false);
        setEditingStatutory(null);
        setStatutoryForm({ name: '', description: '', due_day: 1, due_month: '', category: 'gst', applicable_to: [] });
        fetchData();
      } else {
        const error = await response.json();
        alert('Failed: ' + (error.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error saving statutory date:', error);
    }
  };

  const deleteStatutoryDate = async (dateId) => {
    if (!window.confirm('Delete this statutory date?')) return;
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/statutory-calendar/${dateId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Error deleting statutory date:', error);
    }
  };

  const seedDefaultTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/templates/seed`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const result = await response.json();
        alert(result.message);
        fetchData();
      }
    } catch (error) {
      console.error('Error seeding templates:', error);
    }
  };

  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const url = editingTemplate 
        ? `${API_URL}/api/templates/${editingTemplate.id}`
        : `${API_URL}/api/templates`;
      const method = editingTemplate ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(templateForm)
      });
      
      if (response.ok) {
        setShowTemplateModal(false);
        setEditingTemplate(null);
        setTemplateForm({ name: '', template_type: 'custom', subject: '', body: '', is_active: true });
        fetchData();
      } else {
        const error = await response.json();
        alert('Failed: ' + (error.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error saving template:', error);
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/templates/${templateId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Error deleting template:', error);
    }
  };

  const openEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name,
      template_type: template.template_type,
      subject: template.subject || '',
      body: template.body,
      is_active: template.is_active
    });
    setShowTemplateModal(true);
  };

  const openSendModal = async (reminder) => {
    setSelectedReminder(reminder);
    
    // Use reminder message directly (already formatted with template)
    let message = reminder.message || `This is a reminder regarding ${reminder.title}.\n\nDue Date: ${formatDate(reminder.due_date)}\n\nPlease take necessary action.`;
    
    // Always fetch latest payment link from task and replace old one in message
    if (reminder.task_id) {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/tasks/${reminder.task_id}/payment-link`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.payment_link) {
            // Replace old payment link if present
            message = message.replace(/Pay Online: https:\/\/rzp\.io\/\S+/g, `Pay Online: ${data.payment_link}`);
            // If no payment link in message, append it
            if (!message.includes('rzp.io') && !message.includes('Pay Online')) {
              message += `\n\nPay Online: ${data.payment_link}`;
            }
          }
        }
      } catch (err) {
        console.error('Failed to fetch payment link:', err);
      }
    }
    
    setSendForm({
      message,
      send_email: true,
      send_whatsapp: true
    });
    setShowSendModal(true);
    
    // Generate reminder image
    setLoadingImage(true);
    setReminderImageUrl(null);
    setReminderImageBlob(null);
    const token = localStorage.getItem('token');
    fetch(`${API_URL}/api/reminders/${reminder.id}/image`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => res.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        setReminderImageUrl(url);
        setReminderImageBlob(blob);
      })
      .catch(err => console.error('Image generation failed:', err))
      .finally(() => setLoadingImage(false));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'task_due': return <Clock className="w-4 h-4" />;
      case 'pending_docs': return <FileText className="w-4 h-4" />;
      case 'invoice': case 'receipt': return <DollarSign className="w-4 h-4" />;
      case 'statutory': return <Calendar className="w-4 h-4" />;
      case 'status_update': return <Send className="w-4 h-4" />;
      default: return <Bell className="w-4 h-4" />;
    }
  };

  const getTypeBadge = (type) => {
    const styles = {
      task_due: 'bg-blue-100 text-blue-800',
      pending_docs: 'bg-yellow-100 text-yellow-800',
      invoice: 'bg-green-100 text-green-800',
      receipt: 'bg-emerald-100 text-emerald-800',
      statutory: 'bg-purple-100 text-purple-800',
      status_update: 'bg-teal-100 text-teal-800',
      custom: 'bg-gray-100 text-gray-800'
    };
    return styles[type] || styles.custom;
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      high: 'bg-red-100 text-red-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-gray-100 text-gray-800'
    };
    return styles[priority] || styles.medium;
  };

  const filteredReminders = reminders.filter(r => {
    if (filterStatus && r.status !== filterStatus) return false;
    if (filterType && r.reminder_type !== filterType) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        r.title?.toLowerCase().includes(search) ||
        r.client_name?.toLowerCase().includes(search) ||
        r.task_name?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const ReminderCard = ({ reminder, showActions = true }) => (
    <div data-testid={`reminder-card-${reminder.id}`} className={`bg-white border rounded-lg p-4 ${reminder.status === 'pending' && dashboard.overdue?.some(o => o.id === reminder.id) ? 'border-red-300 bg-red-50' : reminder.status === 'completed' ? 'border-green-200 bg-green-50' : reminder.status === 'sent' ? 'border-blue-200 bg-blue-50' : 'border-gray-200'}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            {getTypeIcon(reminder.reminder_type)}
            <span className={`px-2 py-0.5 text-xs rounded-full ${getTypeBadge(reminder.reminder_type)}`}>
              {reminder.reminder_type.replace('_', ' ')}
            </span>
            <span className={`px-2 py-0.5 text-xs rounded-full ${getPriorityBadge(reminder.priority)}`}>
              {reminder.priority}
            </span>
            {reminder.phase && (
              <span className="px-2 py-0.5 text-xs rounded-full bg-indigo-100 text-indigo-800">
                {reminder.phase === 'pre_work' ? 'Pre-Work' : reminder.phase === 'during_work' ? 'During Work' : reminder.phase === 'work_completion' ? 'Completion' : reminder.phase === 'status_update' ? 'Status Update' : 'Post-Work'}
              </span>
            )}
            <span className={`px-2 py-0.5 text-xs rounded-full ${
              reminder.status === 'completed' ? 'bg-green-100 text-green-800' :
              reminder.status === 'sent' ? 'bg-blue-100 text-blue-800' :
              reminder.status === 'snoozed' ? 'bg-yellow-100 text-yellow-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {reminder.status}
            </span>
          </div>
          <h3 className="font-semibold text-gray-900">{reminder.title}</h3>
          {reminder.client_name && (
            <p className="text-sm text-gray-600 flex items-center mt-1">
              <Building2 className="w-3 h-3 mr-1" />
              {reminder.client_name}
            </p>
          )}
          {reminder.task_name && (
            <p className="text-sm text-gray-500 mt-1">Task: {reminder.task_name}</p>
          )}
          <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
            <span className="flex items-center">
              <Calendar className="w-3 h-3 mr-1" />
              Due: {formatDate(reminder.due_date)}
            </span>
            {reminder.next_reminder_date && (
              <span className="flex items-center">
                <Bell className="w-3 h-3 mr-1" />
                Next: {formatDate(reminder.next_reminder_date)}
              </span>
            )}
          </div>
        </div>
        
        {showActions && reminder.status !== 'completed' && (
          <div className="flex flex-col gap-2 ml-4">
            <button
              onClick={() => openSendModal(reminder)}
              className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 flex items-center"
              data-testid="send-reminder-button"
            >
              <Send className="w-3 h-3 mr-1" />
              Send
            </button>
            <div className="flex gap-1">
              <button
                onClick={() => snoozeReminder(reminder.id, 1)}
                className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded hover:bg-gray-200"
                title="Snooze 1 day"
                data-testid="snooze-1d-button"
              >
                +1d
              </button>
              <button
                onClick={() => snoozeReminder(reminder.id, 3)}
                className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded hover:bg-gray-200"
                title="Snooze 3 days"
                data-testid="snooze-3d-button"
              >
                +3d
              </button>
            </div>
            <button
              onClick={() => updateReminderStatus(reminder.id, 'completed')}
              className="px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700 flex items-center justify-center"
              data-testid="complete-reminder-button"
              title="Mark reminder as completed / stop reminder"
            >
              <CheckCircle className="w-3 h-3 mr-1" />
              Complete
            </button>
            {isPartnerOrManager && (
              <button
                onClick={() => deleteReminder(reminder.id)}
                className="px-3 py-1 bg-red-100 text-red-700 text-xs rounded hover:bg-red-200 flex items-center justify-center"
                data-testid="delete-reminder-button"
                title="Delete reminder permanently"
              >
                <Trash2 className="w-3 h-3 mr-1" />
                Delete
              </button>
            )}
          </div>
        )}
        
        {showActions && reminder.status === 'completed' && (
          <div className="flex flex-col gap-2 ml-4">
            <span className="px-3 py-1.5 bg-green-100 text-green-700 text-sm rounded flex items-center">
              <CheckCircle className="w-3 h-3 mr-1" />
              Completed
            </span>
            {isPartnerOrManager && (
              <button
                onClick={() => deleteReminder(reminder.id)}
                className="px-3 py-1 bg-red-100 text-red-700 text-xs rounded hover:bg-red-200 flex items-center justify-center"
                data-testid="delete-completed-reminder-button"
              >
                <Trash2 className="w-3 h-3 mr-1" />
                Delete
              </button>
            )}
          </div>
        )}
      </div>
      
      {reminder.last_sent_at && (
        <div className="mt-3 pt-3 border-t text-xs text-gray-500">
          Last sent: {formatDate(reminder.last_sent_at)} by {reminder.send_history?.[reminder.send_history.length - 1]?.sent_by_name}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reminders</h1>
          <p className="text-gray-600">Manage and send reminders to clients</p>
        </div>
        <div className="flex gap-2">
          {isPartnerOrManager && (
            <>
              <button
                onClick={() => setShowStatutoryModal(true)}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Statutory Calendar
              </button>
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Reminder
              </button>
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-fit">
        <button
          onClick={() => setActiveTab('dashboard')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'dashboard' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Dashboard
        </button>
        <button
          onClick={() => setActiveTab('all')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'all' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          All Reminders
        </button>
        {isPartnerOrManager && (
          <button
            onClick={() => setActiveTab('templates')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'templates' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
            data-testid="templates-tab"
          >
            Templates
          </button>
        )}
      </div>

      {activeTab === 'dashboard' && (
        <div className="space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-600">Overdue</p>
                  <p className="text-2xl font-bold text-red-700">{dashboard.overdue_count || 0}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-red-400" />
              </div>
            </div>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-orange-600">Today</p>
                  <p className="text-2xl font-bold text-orange-700">{dashboard.today_count || 0}</p>
                </div>
                <Clock className="w-8 h-8 text-orange-400" />
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600">This Week</p>
                  <p className="text-2xl font-bold text-blue-700">{dashboard.upcoming_count || 0}</p>
                </div>
                <Calendar className="w-8 h-8 text-blue-400" />
              </div>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Pending</p>
                  <p className="text-2xl font-bold text-gray-700">{dashboard.total_pending || 0}</p>
                </div>
                <Bell className="w-8 h-8 text-gray-400" />
              </div>
            </div>
          </div>

          {/* Overdue */}
          {dashboard.overdue?.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-red-700 mb-3 flex items-center">
                <AlertTriangle className="w-5 h-5 mr-2" />
                Overdue Reminders
              </h2>
              <div className="grid gap-4">
                {dashboard.overdue.map(reminder => (
                  <ReminderCard key={reminder.id} reminder={reminder} />
                ))}
              </div>
            </div>
          )}

          {/* Today */}
          {dashboard.today?.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-orange-700 mb-3 flex items-center">
                <Clock className="w-5 h-5 mr-2" />
                Today's Reminders
              </h2>
              <div className="grid gap-4">
                {dashboard.today.map(reminder => (
                  <ReminderCard key={reminder.id} reminder={reminder} />
                ))}
              </div>
            </div>
          )}

          {/* Upcoming */}
          {dashboard.upcoming?.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-blue-700 mb-3 flex items-center">
                <Calendar className="w-5 h-5 mr-2" />
                Upcoming This Week
              </h2>
              <div className="grid gap-4">
                {dashboard.upcoming.map(reminder => (
                  <ReminderCard key={reminder.id} reminder={reminder} />
                ))}
              </div>
            </div>
          )}

          {dashboard.total_pending === 0 && (
            <div className="text-center py-12 text-gray-500">
              <Bell className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No pending reminders</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'all' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="bg-white rounded-lg shadow-sm p-4 flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search reminders..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg"
                />
              </div>
            </div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="border rounded-lg px-3 py-2"
            >
              <option value="">All Types</option>
              <option value="task_due">Task Due</option>
              <option value="pending_docs">Pending Docs</option>
              <option value="invoice">Invoice</option>
              <option value="receipt">Receipt</option>
              <option value="status_update">Status Update</option>
              <option value="statutory">Statutory</option>
              <option value="custom">Custom</option>
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="border rounded-lg px-3 py-2"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="sent">Sent</option>
              <option value="snoozed">Snoozed</option>
              <option value="completed">Completed</option>
            </select>
          </div>

          {/* Reminders List */}
          <div className="space-y-4">
            {filteredReminders.length === 0 ? (
              <div className="text-center py-12 text-gray-500 bg-white rounded-lg">
                <Bell className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No reminders found</p>
              </div>
            ) : (
              filteredReminders.map(reminder => (
                <ReminderCard key={reminder.id} reminder={reminder} />
              ))
            )}
          </div>
        </div>
      )}

      {activeTab === 'templates' && isPartnerOrManager && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">
              Manage message templates used for client notifications. Use placeholders: <code className="bg-gray-100 px-1 rounded text-xs">{'{client_name}'}</code> <code className="bg-gray-100 px-1 rounded text-xs">{'{task_name}'}</code> <code className="bg-gray-100 px-1 rounded text-xs">{'{service_name}'}</code> <code className="bg-gray-100 px-1 rounded text-xs">{'{status}'}</code> <code className="bg-gray-100 px-1 rounded text-xs">{'{due_date}'}</code> <code className="bg-gray-100 px-1 rounded text-xs">{'{staff_name}'}</code> <code className="bg-gray-100 px-1 rounded text-xs">{'{firm_name}'}</code>
            </p>
            <div className="flex gap-2">
              <button
                onClick={seedDefaultTemplates}
                className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm flex items-center"
                data-testid="seed-templates-button"
              >
                <RefreshCw className="w-4 h-4 mr-1" />
                Load Defaults
              </button>
              <button
                onClick={() => {
                  setEditingTemplate(null);
                  setTemplateForm({ name: '', template_type: 'custom', subject: '', body: '', is_active: true });
                  setShowTemplateModal(true);
                }}
                className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm flex items-center"
                data-testid="add-template-button"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Template
              </button>
            </div>
          </div>

          {templates.length === 0 ? (
            <div className="text-center py-12 text-gray-500 bg-white rounded-lg border">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="mb-3">No templates yet</p>
              <button
                onClick={seedDefaultTemplates}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Load Default Templates
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {templates.map(template => (
                <div key={template.id} className={`bg-white border rounded-lg p-4 ${!template.is_active ? 'opacity-60' : ''}`} data-testid={`template-card-${template.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900">{template.name}</h3>
                        <span className="px-2 py-0.5 text-xs rounded-full bg-indigo-100 text-indigo-800">
                          {template.template_type.replace('_', ' ')}
                        </span>
                        {!template.is_active && (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">Inactive</span>
                        )}
                      </div>
                      {template.subject && (
                        <p className="text-sm text-gray-600 mb-1">Subject: {template.subject}</p>
                      )}
                      <p className="text-sm text-gray-500 whitespace-pre-line line-clamp-3">{template.body}</p>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() => openEditTemplate(template)}
                        className="px-3 py-1.5 bg-blue-100 text-blue-700 text-sm rounded hover:bg-blue-200 flex items-center"
                        data-testid="edit-template-button"
                      >
                        <Edit className="w-3 h-3 mr-1" />
                        Edit
                      </button>
                      <button
                        onClick={() => deleteTemplate(template.id)}
                        className="px-3 py-1.5 bg-red-100 text-red-700 text-sm rounded hover:bg-red-200 flex items-center"
                        data-testid="delete-template-button"
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Template Create/Edit Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold">{editingTemplate ? 'Edit Template' : 'New Template'}</h2>
                <button onClick={() => { setShowTemplateModal(false); setEditingTemplate(null); }} className="text-gray-400 hover:text-gray-600">
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <form onSubmit={handleSaveTemplate} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Template Name *</label>
                    <input
                      type="text"
                      value={templateForm.name}
                      onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2"
                      placeholder="e.g., Work Completion Notice"
                      required
                      data-testid="template-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Type *</label>
                    <select
                      value={templateForm.template_type}
                      onChange={(e) => setTemplateForm({ ...templateForm, template_type: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2"
                      data-testid="template-type-select"
                    >
                      <option value="work_completion">Work Completion</option>
                      <option value="status_update">Status Update</option>
                      <option value="data_collection">Data Collection</option>
                      <option value="payment_collection">Payment Collection</option>
                      <option value="additional_info">Additional Info</option>
                      <option value="custom">Custom</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Subject</label>
                  <input
                    type="text"
                    value={templateForm.subject}
                    onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="e.g., Work Completed - {task_name}"
                    data-testid="template-subject-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Message Body *</label>
                  <textarea
                    value={templateForm.body}
                    onChange={(e) => setTemplateForm({ ...templateForm, body: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 h-48 resize-none font-mono text-sm"
                    placeholder="Dear {client_name},&#10;&#10;Your work {task_name} is..."
                    required
                    data-testid="template-body-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Placeholders: {'{client_name}'}, {'{task_name}'}, {'{service_name}'}, {'{status}'}, {'{due_date}'}, {'{staff_name}'}, {'{firm_name}'}
                  </p>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="template_active"
                    checked={templateForm.is_active}
                    onChange={(e) => setTemplateForm({ ...templateForm, is_active: e.target.checked })}
                    className="mr-2"
                  />
                  <label htmlFor="template_active" className="text-sm">Active</label>
                </div>
                
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => { setShowTemplateModal(false); setEditingTemplate(null); }}
                    className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    data-testid="save-template-button"
                  >
                    {editingTemplate ? 'Update Template' : 'Create Template'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Create Reminder Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold">Create Reminder</h2>
                <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <form onSubmit={handleCreateReminder} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Type *</label>
                    <select
                      value={reminderForm.reminder_type}
                      onChange={(e) => setReminderForm({ ...reminderForm, reminder_type: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2"
                      required
                    >
                      <option value="task_due">Task Due Date</option>
                      <option value="pending_docs">Pending Documents</option>
                      <option value="invoice">Invoice Follow-up</option>
                      <option value="receipt">Receipt Follow-up</option>
                      <option value="statutory">Statutory Deadline</option>
                      <option value="custom">Custom</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Priority</label>
                    <select
                      value={reminderForm.priority}
                      onChange={(e) => setReminderForm({ ...reminderForm, priority: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Title *</label>
                  <input
                    type="text"
                    value={reminderForm.title}
                    onChange={(e) => setReminderForm({ ...reminderForm, title: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="Reminder title"
                    required
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Client</label>
                    <select
                      value={reminderForm.client_id}
                      onChange={(e) => setReminderForm({ ...reminderForm, client_id: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2"
                    >
                      <option value="">Select Client</option>
                      {clients.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Task</label>
                    <select
                      value={reminderForm.task_id}
                      onChange={(e) => setReminderForm({ ...reminderForm, task_id: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2"
                    >
                      <option value="">Select Task</option>
                      {tasks.filter(t => !reminderForm.client_id || t.client_id === reminderForm.client_id).map(t => (
                        <option key={t.id} value={t.id}>{t.task_name || t.task_type_name} - {t.client_name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Due Date *</label>
                  <input
                    type="date"
                    value={reminderForm.due_date}
                    onChange={(e) => setReminderForm({ ...reminderForm, due_date: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Message</label>
                  <textarea
                    value={reminderForm.message}
                    onChange={(e) => setReminderForm({ ...reminderForm, message: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 h-24 resize-none"
                    placeholder="Reminder message to send to client..."
                  />
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="send_to_client"
                    checked={reminderForm.send_to_client}
                    onChange={(e) => setReminderForm({ ...reminderForm, send_to_client: e.target.checked })}
                    className="mr-2"
                  />
                  <label htmlFor="send_to_client" className="text-sm">Send reminder to client</label>
                </div>
                
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Create Reminder
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Send Reminder Modal */}
      {showSendModal && selectedReminder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Send Reminder</h2>
                <button onClick={() => { setShowSendModal(false); if (reminderImageUrl) URL.revokeObjectURL(reminderImageUrl); }} className="text-gray-400 hover:text-gray-600">
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Left: Image Preview */}
                <div>
                  <label className="block text-sm font-medium mb-2">Reminder Card Image</label>
                  <div className="border rounded-lg overflow-hidden bg-gray-50">
                    {loadingImage ? (
                      <div className="flex items-center justify-center h-48">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <span className="ml-2 text-sm text-gray-500">Generating image...</span>
                      </div>
                    ) : reminderImageUrl ? (
                      <img src={reminderImageUrl} alt="Reminder card" className="w-full" data-testid="reminder-image-preview" />
                    ) : (
                      <div className="flex items-center justify-center h-48 text-gray-400 text-sm">Image not available</div>
                    )}
                  </div>
                  {reminderImageUrl && (
                    <div className="flex gap-2 mt-2">
                      <a
                        href={reminderImageUrl}
                        download={`reminder-${selectedReminder.id}.png`}
                        className="flex-1 px-3 py-2 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700 flex items-center justify-center"
                        data-testid="download-image-button"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Download
                      </a>
                      <button
                        onClick={async () => {
                          if (!reminderImageBlob) return;
                          const file = new File([reminderImageBlob], `reminder-${selectedReminder.id}.png`, { type: 'image/png' });
                          const phone = selectedReminder.client_phone?.replace(/[^0-9]/g, '') || '';
                          const caption = sendForm.message || '';
                          
                          // Try Web Share API (works on mobile PWA)
                          if (navigator.canShare && navigator.canShare({ files: [file] })) {
                            try {
                              await navigator.share({ files: [file], text: caption });
                              return;
                            } catch (err) {
                              if (err.name !== 'AbortError') console.error('Share failed:', err);
                              return;
                            }
                          }
                          
                          // Fallback: open WhatsApp with text (desktop)
                          const phoneNum = phone.startsWith('91') ? phone : '91' + phone;
                          const waUrl = `https://wa.me/${phoneNum}?text=${encodeURIComponent(caption + '\n\n(Please find the reminder image attached separately)')}`;
                          window.open(waUrl, '_blank');
                        }}
                        className="flex-1 px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 flex items-center justify-center"
                        data-testid="share-whatsapp-button"
                      >
                        <MessageSquare className="w-4 h-4 mr-1" />
                        Share to WhatsApp
                      </button>
                    </div>
                  )}
                </div>
                
                {/* Right: Message & Options */}
                <div className="space-y-3">
                  {/* Recipient Info */}
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="font-medium text-sm">{selectedReminder.client_name || 'No client'}</p>
                    <div className="flex flex-col gap-1 text-xs text-gray-600 mt-1">
                      {selectedReminder.client_email && (
                        <span className="flex items-center"><Mail className="w-3 h-3 mr-1" />{selectedReminder.client_email}</span>
                      )}
                      {selectedReminder.client_phone && (
                        <span className="flex items-center"><MessageSquare className="w-3 h-3 mr-1" />{selectedReminder.client_phone}</span>
                      )}
                    </div>
                  </div>
                  
                  {/* Message */}
                  <div>
                    <label className="block text-sm font-medium mb-1">Message (Editable)</label>
                    <textarea
                      value={sendForm.message}
                      onChange={(e) => setSendForm({ ...sendForm, message: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2 h-32 resize-none text-sm"
                    />
                  </div>
                  
                  {/* Send Options */}
                  <div className="space-y-2">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={sendForm.send_email}
                        onChange={(e) => setSendForm({ ...sendForm, send_email: e.target.checked })}
                        className="mr-2 w-4 h-4"
                        data-testid="send-email-checkbox"
                      />
                      <Mail className="w-4 h-4 mr-1" />
                      <span className="text-sm">Email</span>
                      {!selectedReminder.client_email && (
                        <span className="ml-2 text-xs text-red-500">(No email on file)</span>
                      )}
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={sendForm.send_whatsapp}
                        onChange={(e) => setSendForm({ ...sendForm, send_whatsapp: e.target.checked })}
                        className="mr-2 w-4 h-4"
                        data-testid="send-whatsapp-checkbox"
                      />
                      <MessageSquare className="w-4 h-4 mr-1" />
                      <span className="text-sm">WhatsApp</span>
                      {!selectedReminder.client_phone && (
                        <span className="ml-2 text-xs text-red-500">(No phone on file)</span>
                      )}
                    </label>
                    {sendForm.send_whatsapp && reminderImageUrl && (
                      <p className="text-xs text-gray-500 ml-6">Use "Share to WhatsApp" button below the image to send it directly with the image.</p>
                    )}
                  </div>
                  
                  {/* Signature */}
                  <div className="bg-blue-50 rounded-lg p-2 text-sm">
                    <div className="flex items-center gap-2">
                      <img src="/images/ca-logo.png" alt="CA" className="h-6 w-auto" />
                      <div>
                        <p className="font-medium text-xs">{user?.full_name}</p>
                        <p className="text-blue-800 font-semibold text-[10px]">HYDER ALI AND ASSOCIATES</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
                <button
                  onClick={() => { setShowSendModal(false); if (reminderImageUrl) URL.revokeObjectURL(reminderImageUrl); setReminderImageBlob(null); }}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendReminder}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
                  data-testid="send-reminder-submit"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send Reminder
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Statutory Calendar Modal */}
      {showStatutoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold">Statutory Calendar</h2>
                <button onClick={() => setShowStatutoryModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              {statutoryDates.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-500 mb-4">No statutory dates configured</p>
                  <button
                    onClick={seedStatutoryDates}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                  >
                    Load Default Dates (GST, TDS, IT, ROC)
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={seedStatutoryDates}
                      className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-sm flex items-center"
                    >
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Load Defaults
                    </button>
                    {isPartnerOrManager && (
                      <button
                        onClick={() => {
                          setEditingStatutory(null);
                          setStatutoryForm({ name: '', description: '', due_day: 1, due_month: '', category: 'gst', applicable_to: [] });
                          setShowStatutoryEditModal(true);
                        }}
                        className="px-3 py-1.5 bg-purple-600 text-white rounded hover:bg-purple-700 text-sm flex items-center"
                        data-testid="add-statutory-date-button"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add New
                      </button>
                    )}
                  </div>
                  
                  {['gst', 'tds', 'income_tax', 'roc', 'other'].map(category => {
                    const categoryDates = statutoryDates.filter(d => d.category === category);
                    if (categoryDates.length === 0) return null;
                    
                    return (
                      <div key={category}>
                        <h3 className="font-semibold text-gray-700 mb-2 uppercase text-sm">
                          {category.replace('_', ' ')}
                        </h3>
                        <div className="bg-gray-50 rounded-lg divide-y">
                          {categoryDates.map(date => (
                            <div key={date.id} className="p-3 flex justify-between items-center">
                              <div>
                                <p className="font-medium">{date.name}</p>
                                <p className="text-sm text-gray-500">
                                  {date.due_month ? `Day ${date.due_day} of Month ${date.due_month}` : `Day ${date.due_day} every month`}
                                  {date.description && ` — ${date.description}`}
                                </p>
                              </div>
                              {isPartnerOrManager && (
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => openEditStatutory(date)}
                                    className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded hover:bg-blue-200"
                                    data-testid="edit-statutory-button"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => deleteStatutoryDate(date.id)}
                                    className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded hover:bg-red-200"
                                    data-testid="delete-statutory-button"
                                  >
                                    Delete
                                  </button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Statutory Date Add/Edit Modal */}
      {showStatutoryEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-bold">{editingStatutory ? 'Edit Statutory Date' : 'Add Statutory Date'}</h2>
                <button onClick={() => { setShowStatutoryEditModal(false); setEditingStatutory(null); }} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={handleSaveStatutory} className="space-y-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Name *</label>
                  <input type="text" value={statutoryForm.name} onChange={(e) => setStatutoryForm({ ...statutoryForm, name: e.target.value })} className="w-full border rounded-lg px-3 py-2" required data-testid="statutory-name-input" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Due Day *</label>
                    <select value={statutoryForm.due_day} onChange={(e) => setStatutoryForm({ ...statutoryForm, due_day: parseInt(e.target.value) })} className="w-full border rounded-lg px-3 py-2" data-testid="statutory-day-select">
                      {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Month</label>
                    <select value={statutoryForm.due_month} onChange={(e) => setStatutoryForm({ ...statutoryForm, due_month: e.target.value })} className="w-full border rounded-lg px-3 py-2" data-testid="statutory-month-select">
                      <option value="">Every Month</option>
                      {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, i) => (
                        <option key={i+1} value={i+1}>{m}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Category *</label>
                    <select value={statutoryForm.category} onChange={(e) => setStatutoryForm({ ...statutoryForm, category: e.target.value })} className="w-full border rounded-lg px-3 py-2" data-testid="statutory-category-select">
                      <option value="gst">GST</option>
                      <option value="tds">TDS</option>
                      <option value="income_tax">Income Tax</option>
                      <option value="roc">ROC</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <input type="text" value={statutoryForm.description} onChange={(e) => setStatutoryForm({ ...statutoryForm, description: e.target.value })} className="w-full border rounded-lg px-3 py-2" placeholder="Brief description" data-testid="statutory-desc-input" />
                </div>
                <div className="flex justify-end gap-3 pt-3 border-t">
                  <button type="button" onClick={() => { setShowStatutoryEditModal(false); setEditingStatutory(null); }} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
                  <button type="submit" className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700" data-testid="save-statutory-button">{editingStatutory ? 'Update' : 'Create'}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reminders;
